
import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Autocomplete,
  IconButton
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { setIsNewTut, setTutCurrentItem } from "../../../../redux/slices/tutorialEditSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";


import MachineListAPIs from "../../../../utilities/api/machine/AdminMachineList";
import EquipmentListAPIs from "../../../../utilities/api/equipment/AdminEquipmentList";
import InventoryListAPIs from "../../../../utilities/api/inventory/AdminInventoryList";
import AdminModuleAPIs from "../../../../utilities/api/tutorials/AdminModuleAPIs";
import AdminVideoAPIs from "../../../../utilities/api/tutorials/AdminVideoAPIs";

const AddModuleForm = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.user);

  /* ---------------- STATE ---------------- */

  const [machines, setMachines] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [inventories, setInventories] = useState([]);
 const [videos, setVideos] = useState([
  { title: "", link: "", length: "" }
]);

  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [document, setDocument] = useState(null);
  const [videoErrors, setVideoErrors] = useState([{}]);

  /* ---------------- VALIDATION ---------------- */

  const validationSchema = Yup.object({
    name: Yup.string().required("Module name required"),
    description: Yup.string().required("Description required")
  });

  /* ---------------- LOAD DROPDOWN DATA ---------------- */

  useEffect(() => {
    const load = async () => {
      try {
        const [m, e, i] = await Promise.all([
          MachineListAPIs.MachineListGet(),
          EquipmentListAPIs.EquipmentListGet(),
          InventoryListAPIs.InventoryListGet({
            refresh_token: user.refresh,
            access_token: user.access
          })
        ]);

        setMachines(m || []);
        setEquipments(e || []);
        setInventories(i || []);
      } catch (err) {
        console.error("Dropdown error:", err);
      }
    };

    load();
  }, [user]);

  
  const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

  /* ---------------- VIDEO HANDLERS ---------------- */

const addVideo = () => {
  setVideos(prev => [...prev, { title: "", link: "", length: "" }]);
  setVideoErrors(prev => [...prev, {}]);
};



const removeVideo = (index) => {
  setVideos(prev => prev.filter((_, i) => i !== index));
  setVideoErrors(prev => prev.filter((_, i) => i !== index));
};


  const updateVideo = (index, field, value) => {
    setVideos(prev => {
      const copy = [...prev];
      copy[index][field] = value;
      return copy;
    });
  };

  /* ---------------- FORMIK ---------------- */

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      related_machines: [],
      related_equipments: [],
      related_inventory: []
    },
    validationSchema,

onSubmit: async values => {
  setLoading(true);

  try {
    // ✅ 1️⃣ Validate all video fields FIRST
/* ===========================
   🔥 Video Validations
=========================== */

const links = videos.map(v => v.link.trim());

// 1️⃣ Empty field validation
for (let i = 0; i < videos.length; i++) {
  const v = videos[i];

  if (!v.title.trim() || !v.link.trim() || !v.length) {
    toast.error(`Video ${i + 1}: All fields are required.`);
    setLoading(false);
    return;
  }
}

// 2️⃣ Invalid URL validation
for (let i = 0; i < links.length; i++) {
  if (!isValidURL(links[i])) {
    toast.error(`Video ${i + 1}: Invalid URL format.`);
    setLoading(false);
    return;
  }
}

// 3️⃣ Duplicate link validation
const uniqueLinks = new Set(links);

if (uniqueLinks.size !== links.length) {
  toast.error("Duplicate video links are not allowed.");
  setLoading(false);
  return;
}


    //  Create Videos
    const createdVideos = await Promise.all(
      videos.map(v => {
        const totalMinutes = parseInt(v.length || 0);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const formattedLength = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}:00`;

        return AdminVideoAPIs.CreateVideoPost({
          title: v.title.trim(),
          description: v.title.trim(),
          link: v.link.trim(),
          video_length: formattedLength
        });
      })
    );

    console.log("Created Videos:", createdVideos);

    //  Create Module with video_id (IMPORTANT)
    const modulePayload = {
      name: values.name,
      description: values.description,
      related_machines: values.related_machines.map(m => m.id),
      related_equipments: values.related_equipments.map(e => e.id),
      related_inventory: values.related_inventory.map(i => i.id),
      videos: createdVideos.map((v, index) => ({
        video_id: v.id,   // 🔥 MUST BE video_id
        order: index + 1
      }))
    };

    const createdModule = await AdminModuleAPIs.CreateModulePost(modulePayload);

    console.log("Created Module:", createdModule);

    //  Upload Files (if needed)
    if (image || document) {
      const fileData = new FormData();
      if (image) fileData.append("image", image);
      if (document) fileData.append("document", document);

      await AdminModuleAPIs.UpdateModule(createdModule.id, fileData);
    }

    dispatch(setTutCurrentItem(createdModule));
    toast.success("Module created successfully");

    formik.resetForm();
    setVideos([{ title: "", link: "", length: "" }]);
    setImage(null);
    setDocument(null);
    dispatch(setIsNewTut(false));

  } catch (err) {
    console.error("MODULE ERROR:", err.response?.data || err);
    toast.error("Failed to create module.");
  }

  setLoading(false);
}
  });



  /* ---------------- UI ---------------- */

  return (

    
    <Box component="form" onSubmit={formik.handleSubmit} p={3}>
      <Box display="flex" alignItems="center" mb={2}>
  <IconButton
    onClick={() => dispatch(setIsNewTut(false))}
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      mr: 2,
    }}
  >
    <ArrowBackIcon />
  </IconButton>

  <Typography variant="h6" fontWeight={600}>
    Add Module
  </Typography>
</Box>

 {/* VIDEOS */}
<Typography fontWeight={600} mb={2}>
  Videos
</Typography>

{videos.map((v, i) => (
  <Box
    key={i}
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: 2,
      p: 2,
      mb: 3,
      backgroundColor: "#fafafa",
    }}
  >
    {/* Title */}

<TextField
  label="Video Title"
  value={v.title}
  onChange={(e) => updateVideo(i, "title", e.target.value)}
  fullWidth
  error={Boolean(videoErrors[i]?.title)}
  helperText={videoErrors[i]?.title}
  sx={{ mb: 2 }}
/>


    {/* Video Link */}
<TextField
  label="Video Link"
  value={v.link}
  onChange={(e) => updateVideo(i, "link", e.target.value)}
  fullWidth
  error={Boolean(videoErrors[i]?.link)}
  helperText={videoErrors[i]?.link}
  sx={{ mb: 2 }}
/>


    {/* Length + Delete Row */}
<TextField
  label="Length (minutes)"
  type="number"
  value={v.length}
  onChange={e => updateVideo(i, "length", e.target.value)}
  inputProps={{ min: 0 }}
  fullWidth
  error={Boolean(videoErrors[i]?.length)}
  helperText={videoErrors[i]?.length}
/>


    <Box display="flex" flexDirection="column" gap={2}>

      <IconButton
        color="error"
        onClick={() => removeVideo(i)}
        sx={{ ml: "auto" }}
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  </Box>
))}

<Button
  variant="outlined"
  startIcon={<AddIcon />}
  onClick={addVideo}
>
  Add Video
</Button>

      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Add Module</Typography>

      
      </Box>

      {/* NAME */}
      <TextField
        label="Module Name"
        name="name"
        fullWidth
        value={formik.values.name}
        onChange={formik.handleChange}
        error={formik.touched.name && Boolean(formik.errors.name)}
        helperText={formik.touched.name && formik.errors.name}
        sx={{ mb: 2 }}
      />

      {/* DESCRIPTION */}
      <TextField
        label="Description"
        name="description"
        multiline
        rows={4}
        fullWidth
        value={formik.values.description}
        onChange={formik.handleChange}
        error={formik.touched.description && Boolean(formik.errors.description)}
        helperText={formik.touched.description && formik.errors.description}
        sx={{ mb: 3 }}
      />
      {/* DOCUMENT */}
<Typography fontWeight={600}>Module Document</Typography>

<Button variant="outlined" component="label" sx={{ mb: 3 }}>
  Upload Document
  <input
    hidden
    type="file"
    accept=".pdf,.doc,.docx"
    onChange={e => setDocument(e.target.files[0])}
  />
</Button>

{document && (
  <Typography mb={3} fontSize={14}>
    Selected: {document.name}
  </Typography>
)}

      {/* IMAGE */}
      <Typography fontWeight={600}>Module Image</Typography>

      <Button variant="outlined" component="label" sx={{ mb: 3 }}>
        Upload Image
        <input
          hidden
          type="file"
          accept="image/*"
          onChange={e => setImage(e.target.files[0])}
        />
      </Button>

      {image && (
        <Typography mb={3} fontSize={14}>
          Selected: {image.name}
        </Typography>
      )}

      {/* MACHINES */}
      <Autocomplete
        multiple
        options={machines}
        getOptionLabel={o => o.name}
        value={formik.values.related_machines}
        onChange={(e, val) =>
          formik.setFieldValue("related_machines", val)
        }
        renderInput={params => <TextField {...params} label="Machines" />}
        sx={{ mb: 3 }}
      />

      {/* EQUIPMENTS */}
      <Autocomplete
        multiple
        options={equipments}
        getOptionLabel={o => o.name}
        value={formik.values.related_equipments}
        onChange={(e, val) =>
          formik.setFieldValue("related_equipments", val)
        }
        renderInput={params => <TextField {...params} label="Equipments" />}
        sx={{ mb: 3 }}
      />

      {/* INVENTORY */}
      <Autocomplete
        multiple
        options={inventories}
        getOptionLabel={o => o.name}
        value={formik.values.related_inventory}
        onChange={(e, val) =>
          formik.setFieldValue("related_inventory", val)
        }
        renderInput={params => <TextField {...params} label="Inventory" />}
        sx={{ mb: 4 }}
      />
  <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => dispatch(setIsNewTut(false))}
          >
            Cancel
          </Button>

          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </Box>
   

    </Box>
  );
};

export default AddModuleForm;

